import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Phone, Mail, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

const URL_REGEX = /(https?:\/\/[^\s)<>]+)/g;

const linkifyLine = (text: string): React.ReactNode[] => {
  const parts = text.split(URL_REGEX);
  return parts.map((part, idx) =>
    URL_REGEX.test(part) ? (
      <a key={idx} href={part} target="_blank" rel="noopener noreferrer" className="underline text-primary hover:text-primary/80">
        {part}
      </a>
    ) : (
      <React.Fragment key={idx}>{part}</React.Fragment>
    )
  );
};

interface SessionState {
  campaign: string;
  orientation: Record<string, boolean>;
  collected: Record<string, boolean>;
  values: Record<string, any>;
  branches: Record<string, boolean>;
  lead: { score: number; temperature: string; recommended_action: string };
  prefill_applied?: boolean;
  [key: string]: any;
}

interface PrefillData {
  age?: number;
  gender?: string;
  smoker_status?: string;
  name?: string;
  email?: string;
  phone?: string;
  prefill_source?: string;
  confirm_mode?: boolean;
}

interface CampaignMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  type?: 'text' | 'quickReply' | 'quote';
  quick_replies?: string[];
  quote?: { weekly_premium: number; age_band_used: number; under_35_applied: boolean };
}

export interface CampaignChatUIProps {
  campaignSlug: string;
  welcomeMessage?: string;
  brandColor?: string;
  botName?: string;
  tenantDisplayName?: string;
  sourceChannel?: string;
  orientationOptions?: string[];
  disclaimerText?: string;
  quotePolicyFeeNote?: string;
  prefill?: PrefillData;
  ctaLabels?: {
    arrange?: string;
    brochure?: string;
    keepChatting?: string;
  };
}

const CampaignChatUI: React.FC<CampaignChatUIProps> = ({
  campaignSlug,
  welcomeMessage = "Hi — I'm here to help. How can I assist you today?",
  brandColor,
  botName = "Campaign Assistant",
  tenantDisplayName = "",
  sourceChannel = "web",
  orientationOptions = [],
  disclaimerText = "General information only — not personalised financial advice.",
  prefill,
  ctaLabels,
}) => {
  const [messages, setMessages] = useState<CampaignMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => uuidv4());
  const [sessionState, setSessionState] = useState<SessionState | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!initializedRef.current) {
      setMessages([{
        id: uuidv4(),
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date(),
        type: orientationOptions.length > 0 ? 'quickReply' : 'text',
        quick_replies: orientationOptions.length > 0 ? orientationOptions : undefined,
      }]);
      initializedRef.current = true;
    }
  }, [welcomeMessage, orientationOptions]);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: CampaignMessage = {
      id: uuidv4(),
      role: 'user',
      content,
      timestamp: new Date(),
      type: 'text',
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const history = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      // Include prefill on first message if available and not yet applied
      const prefillPayload = prefill && !sessionState?.prefill_applied ? {
        prefill_values: {
          age: prefill.age,
          gender: prefill.gender,
          smoker_status: prefill.smoker_status,
          name: prefill.name,
          email: prefill.email,
          phone: prefill.phone,
        },
        prefill_confirm_mode: prefill.confirm_mode ?? true,
        prefill_source: prefill.prefill_source,
      } : {};

      // Email lookup fallback: if no prefill is active and user provides an email,
      // send it as an email_lookup_request so the backend can try to match
      let emailLookupPayload = {};
      if (!prefill && !sessionState?.prefill_applied && !sessionState?.email_lookup_attempted) {
        const emailMatch = content.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/);
        if (emailMatch) {
          emailLookupPayload = {
            email_lookup_request: { email: emailMatch[0] },
          };
        }
      }

      const { data, error } = await supabase.functions.invoke('campaign-chat', {
        body: {
          campaign_slug: campaignSlug,
          message: content,
          conversation_history: history,
          session_id: sessionId,
          session_state: sessionState,
          source_channel: sourceChannel,
          ...prefillPayload,
          ...emailLookupPayload,
        },
      });

      if (error) {
        console.error('Campaign chat error:', error);
        setMessages(prev => [...prev, {
          id: uuidv4(),
          role: 'assistant',
          content: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
          timestamp: new Date(),
          type: 'text',
        }]);
        return;
      }

      if (data.session_state) setSessionState(data.session_state);

      const assistantMessage: CampaignMessage = {
        id: uuidv4(),
        role: 'assistant',
        content: data.answer,
        timestamp: new Date(),
        type: data.message_type || 'text',
        quick_replies: data.quick_replies,
        quote: data.quote,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Error:', err);
      setMessages(prev => [...prev, {
        id: uuidv4(),
        role: 'assistant',
        content: "I apologize, but I encountered an error. Please try again.",
        timestamp: new Date(),
        type: 'text',
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [campaignSlug, isLoading, messages, sessionId, sessionState, sourceChannel]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleQuickReply = (reply: string) => {
    sendMessage(reply);
  };

  const arrangeLabel = ctaLabels?.arrange || "Arrange a conversation";
  const brochureLabel = ctaLabels?.brochure || "Send brochure by email";
  const keepChattingLabel = ctaLabels?.keepChatting || "Keep chatting";

  return (
    <div className="flex flex-col h-full bg-background rounded-xl border shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-brand-navy text-white px-6 py-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-brand-teal/20 flex items-center justify-center">
          <MessageSquare className="h-5 w-5 text-brand-teal" />
        </div>
        <div>
          <h3 className="font-semibold text-base">{botName}</h3>
          {tenantDisplayName && (
            <p className="text-xs text-white/70">{tenantDisplayName}</p>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
        {messages.map((message, messageIndex) => {
          const isLatestAssistant = message.role === 'assistant' && 
            messageIndex === messages.map((m, i) => m.role === 'assistant' ? i : -1).filter(i => i >= 0).pop();
          return (
          <div key={message.id}>
            <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  message.role === 'user'
                    ? 'bg-brand-navy text-white rounded-br-md'
                    : 'bg-card border border-border text-card-foreground rounded-bl-md shadow-sm'
                }`}
              >
                <div className="whitespace-pre-wrap">
                  {message.content.split('\n').map((line, i, lines) => (
                    <span key={i} className="contents">
                      {linkifyLine(line)}
                      {i < lines.length - 1 && <br />}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Quote block */}
            {message.quote && (
              <div className="mt-3 ml-2">
                <div className="bg-brand-teal/10 border border-brand-teal/30 rounded-xl p-4 max-w-[85%]">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-brand-teal" />
                    <span className="text-xs font-medium text-brand-teal uppercase tracking-wider">Indicative Quote</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    ${message.quote.weekly_premium.toFixed(2)}
                    <span className="text-sm font-normal text-muted-foreground ml-1">/ week</span>
                  </div>
                  {message.quote.under_35_applied && (
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      For anyone younger than 35, the same pricing band as age 35 currently applies.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Indicative only — exact options confirmed during the application process.
                  </p>
                </div>
              </div>
            )}

            {/* Quick replies */}
            {message.quick_replies && message.quick_replies.length > 0 && isLatestAssistant && !isLoading && (
              <div className="mt-3 ml-2 flex flex-wrap gap-2">
                {message.quick_replies.map((reply) => (
                  <button
                    key={reply}
                    onClick={() => handleQuickReply(reply)}
                    disabled={isLoading}
                    className="px-4 py-2 text-sm rounded-full border border-brand-teal/40 text-brand-teal bg-brand-teal/5 hover:bg-brand-teal/15 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}

            {/* CTA buttons after quote */}
            {message.quote && isLatestAssistant && !isLoading && (
              <div className="mt-3 ml-2 flex flex-wrap gap-2">
                <button
                  onClick={() => sendMessage("I'd like to speak with someone from the team")}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 text-sm rounded-full bg-brand-teal text-white hover:bg-brand-teal/90 transition-colors disabled:opacity-50"
                >
                  <Phone className="h-3.5 w-3.5" />
                  {arrangeLabel}
                </button>
                <button
                  onClick={() => sendMessage("Could you send me the brochure by email?")}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 text-sm rounded-full border border-brand-teal/40 text-brand-teal hover:bg-brand-teal/10 transition-colors disabled:opacity-50"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {brochureLabel}
                </button>
                <button
                  onClick={() => sendMessage("I have more questions about how it works")}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 text-sm rounded-full border border-border text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  {keepChattingLabel}
                </button>
              </div>
            )}
          </div>
          );
        })}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:-0.3s]" />
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:-0.15s]" />
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce" />
                </div>
                <span className="text-sm text-muted-foreground">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t bg-card">
        <div className="flex items-end gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className="min-h-[48px] max-h-[120px] resize-none text-sm"
            disabled={isLoading}
          />
          <Button
            size="icon"
            className="bg-brand-teal hover:bg-brand-teal/90 h-12 w-12 shrink-0"
            onClick={() => sendMessage(input)}
            disabled={isLoading || !input.trim()}
          >
            <Send className="h-5 w-5" />
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground text-center mt-2">
          {disclaimerText}
        </p>
      </div>
    </div>
  );
};

export default CampaignChatUI;
